import { useEffect, useMemo, useState } from 'react';
import { ApiError } from '@/api/client';
import { questionApi } from '@/api/endpoints';
import type { Matchday, Question, QuestionAnswer, QuestionSolution, Team } from '@/api/types';
import { Alert, Button, Card, Notice } from '@/components/ui';
import { formatDateTime } from '@/lib/datetime';
import styles from '@/features/bonus/bonus.module.css';

export interface QuestionsBlockData {
  matchday: Matchday;
  questions: Question[];
  myAnswers: QuestionAnswer[];
  solution: QuestionSolution[];
  deadlinePassed: boolean;
  teams: Team[];
}

/** Draft je Frage: ein Slot bei team/player/free/multiple_choice, slot_count Slots bei team_ranking. */
type Draft = string[];

function slotsFor(q: Question): number {
  return q.answer_type === 'team_ranking' ? (q.slot_count ?? 0) : 1;
}

/**
 * Fragen-Block (Tipp-Formular) für die Fragen eines Spieltags — egal ob Bonus-
 * Spieltag oder eine individuelle Frage an einem normalen Spieltag (liga/pokal).
 * Bei answer_type='team' ein Team-Dropdown, 'team_ranking' ein Dropdown je Platz,
 * 'multiple_choice' ein Dropdown über die frei formulierten Antwortoptionen der
 * Frage. Nach Tippschluss: eigene Antwort + Lösung + Punkte.
 *
 * `showHeader=false` unterdrückt Tippschluss-Zeile + Sperr-Notice — für die
 * Einbettung unter dem Spielformular der Tippabgabe, das beides schon anzeigt.
 */
export function QuestionsBlock({
  data,
  showHeader = true,
}: {
  data: QuestionsBlockData;
  showHeader?: boolean;
}) {
  const { matchday, questions, myAnswers, solution, deadlinePassed, teams } = data;
  const locked = deadlinePassed;

  const teamName = useMemo(() => {
    const map = new Map<number, string>();
    for (const t of teams) map.set(t.id, t.short_name ?? t.name);
    return (id: number | null | undefined): string =>
      id == null ? '—' : (map.get(id) ?? `#${id}`);
  }, [teams]);

  const optionLabel = useMemo(() => {
    const map = new Map<number, string>();
    for (const q of questions) {
      for (const o of q.options ?? []) map.set(o.id, o.label);
    }
    return (id: number | null | undefined): string =>
      id == null ? '—' : (map.get(id) ?? `#${id}`);
  }, [questions]);

  const answerByQuestion = useMemo(() => {
    const map = new Map<number, QuestionAnswer>();
    for (const a of myAnswers) map.set(a.question_id, a);
    return map;
  }, [myAnswers]);

  const solutionByQuestion = useMemo(() => {
    const map = new Map<number, QuestionSolution>();
    for (const s of solution) map.set(s.question_id, s);
    return map;
  }, [solution]);

  const initialDrafts = useMemo<Record<number, Draft>>(() => {
    const drafts: Record<number, Draft> = {};
    for (const q of questions) {
      const a = answerByQuestion.get(q.id);
      const slots = slotsFor(q);
      if (q.answer_type === 'team_ranking') {
        const ids = a?.ranking_team_ids ?? [];
        drafts[q.id] = Array.from({ length: slots }, (_, i) => {
          const v = ids[i];
          return v != null ? String(v) : '';
        });
      } else if (q.answer_type === 'multiple_choice') {
        drafts[q.id] = [a?.option_id != null ? String(a.option_id) : ''];
      } else {
        drafts[q.id] = [a?.team_id != null ? String(a.team_id) : ''];
      }
    }
    return drafts;
  }, [questions, answerByQuestion]);

  const [drafts, setDrafts] = useState(initialDrafts);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [savedMsg, setSavedMsg] = useState<string | null>(null);

  useEffect(() => setDrafts(initialDrafts), [initialDrafts]);

  function handleChange(questionId: number, slotIndex: number, value: string) {
    setSavedMsg(null);
    setSaveError(null);
    setDrafts((prev) => {
      const current = [...(prev[questionId] ?? [])];
      current[slotIndex] = value;
      return { ...prev, [questionId]: current };
    });
  }

  async function handleSave() {
    setSaving(true);
    setSaveError(null);
    setSavedMsg(null);
    try {
      let saved = 0;
      for (const q of questions) {
        const v = drafts[q.id] ?? [];
        if (q.answer_type === 'team_ranking') {
          if (v.some((slot) => slot === '')) continue;
          await questionApi.answer(q.id, { team_ids: v.map((slot) => Number(slot)) });
        } else if (q.answer_type === 'multiple_choice') {
          if (v[0] === undefined || v[0] === '') continue;
          await questionApi.answer(q.id, { option_id: Number(v[0]) });
        } else {
          if (v[0] === undefined || v[0] === '') continue;
          await questionApi.answer(q.id, { team_id: Number(v[0]) });
        }
        saved += 1;
      }
      setSavedMsg(saved === 0 ? 'Keine Frage beantwortet.' : `${saved} Antwort(en) gespeichert.`);
    } catch (err) {
      if (err instanceof ApiError && err.status === 423) {
        setSaveError('Tippschluss überschritten — Antworten sind gesperrt.');
      } else {
        setSaveError(err instanceof Error ? err.message : 'Speichern fehlgeschlagen.');
      }
    } finally {
      setSaving(false);
    }
  }

  if (questions.length === 0) return null;

  return (
    <>
      {showHeader && (
        <p className="muted">Tippschluss: {formatDateTime(matchday.deadline_at)}</p>
      )}

      {showHeader && locked && (
        <Notice>
          Der Tippschluss ist überschritten. Du kannst keine Antworten mehr abgeben oder ändern.
        </Notice>
      )}

      {questions.map((q) => {
        const own = answerByQuestion.get(q.id);
        const sol = solutionByQuestion.get(q.id);
        const draft = drafts[q.id] ?? [];
        const isRanking = q.answer_type === 'team_ranking';
        const isMultipleChoice = q.answer_type === 'multiple_choice';
        const slots = slotsFor(q);

        return (
          <Card key={q.id}>
            <div className={styles.questionPrompt}>{q.prompt}</div>
            <div className={styles.questionMeta}>
              {q.points} Punkte{isRanking ? ' / richtig platziertes Team' : ''}
            </div>

            {isRanking ? (
              <div className={styles.rankingSlots}>
                {Array.from({ length: slots }, (_, i) => (
                  <label key={i} className={styles.field}>
                    <span className={styles.label}>Platz {i + 1}</span>
                    <select
                      className={styles.select}
                      disabled={locked}
                      value={draft[i] ?? ''}
                      onChange={(e) => handleChange(q.id, i, e.target.value)}
                    >
                      <option value="">— Mannschaft wählen —</option>
                      {teams.map((t) => (
                        <option key={t.id} value={String(t.id)}>
                          {t.name}
                        </option>
                      ))}
                    </select>
                  </label>
                ))}
              </div>
            ) : isMultipleChoice ? (
              <label className={styles.field}>
                <span className={styles.label}>Deine Antwort</span>
                <select
                  className={styles.select}
                  disabled={locked}
                  value={draft[0] ?? ''}
                  onChange={(e) => handleChange(q.id, 0, e.target.value)}
                >
                  <option value="">— wählen —</option>
                  {(q.options ?? []).map((o) => (
                    <option key={o.id} value={String(o.id)}>
                      {o.label}
                    </option>
                  ))}
                </select>
              </label>
            ) : (
              <label className={styles.field}>
                <span className={styles.label}>Deine Antwort</span>
                <select
                  className={styles.select}
                  disabled={locked}
                  value={draft[0] ?? ''}
                  onChange={(e) => handleChange(q.id, 0, e.target.value)}
                >
                  <option value="">— Mannschaft wählen —</option>
                  {teams.map((t) => (
                    <option key={t.id} value={String(t.id)}>
                      {t.name}
                    </option>
                  ))}
                </select>
              </label>
            )}

            {locked && (
              <div className={styles.solutionLine}>
                {isRanking ? (
                  <span>
                    Lösung:{' '}
                    <strong>
                      {Array.from({ length: slots }, (_, i) =>
                        teamName(sol?.correct_ranking_team_ids?.[i]),
                      ).join(' · ')}
                    </strong>
                  </span>
                ) : isMultipleChoice ? (
                  <span>
                    Lösung:{' '}
                    <strong>
                      {(sol?.correct_option_ids ?? []).length > 0
                        ? (sol?.correct_option_ids ?? []).map((id) => optionLabel(id)).join(' oder ')
                        : '—'}
                    </strong>
                  </span>
                ) : (
                  <span>
                    Lösung: <strong>{teamName(sol?.correct_team_id)}</strong>
                  </span>
                )}
                {own?.points != null && (
                  <span className={styles.pointsBadge}>{own.points} Pkt.</span>
                )}
              </div>
            )}
          </Card>
        );
      })}

      {!locked && (
        <div className={styles.saveBar}>
          {saveError && <Alert>{saveError}</Alert>}
          <Button onClick={() => void handleSave()} disabled={saving}>
            {saving ? 'Speichern…' : 'Antworten speichern'}
          </Button>
          {savedMsg && <div className={styles.savedHint}>{savedMsg}</div>}
        </div>
      )}
    </>
  );
}
